import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import './ModalProvider.scss';
import Draggable from 'react-draggable';

export interface defalutModalContentProps {
  close: () => void;
}

interface ModalProviderProps {
  children: React.ReactNode;
}

export interface ModalData {
  id: string;
  content: React.ReactNode;
  onClose?: () => void;
  // 클릭 외부 감지를 위한 콘텐츠 컨테이너 ref
  ref?: React.RefObject<HTMLDivElement>;
  // Draggable의 nodeRef로 쓸 ref (findDOMNode 경고 방지)
  dragRef?: React.RefObject<HTMLDivElement>;
  draggable?: boolean;
  disableEscape?: boolean;
}

interface ModalContextType {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void; // 유지 (외부 호환)
  openModal: (
    content: React.ReactNode,
    draggable?: boolean,
    onClose?: () => void,
    disableEscape?: boolean,
  ) => string;
  updateModalContent: (id: string, newContent: React.ReactNode) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  closeTopNModals: (n: number) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<ModalData[]>([]);

  // 파생 값: 별도 state로 보관하지 말고 계산해서 넘깁니다.
  const isModalOpen = modals.length > 0;

  // 외부 호환을 위해 남겨두지만, 내부에선 사용하지 않습니다.
  const setModalOpen = useCallback((open: boolean) => {
    if (!open) {
      // false로 설정하면 모두 닫기
      setModals((prev) => {
        prev.forEach((m) => m.onClose?.());
        return [];
      });
    }
    // true로 설정하는 건 의미 없음(무언가 열려 있어야만 true가 됨)
  }, []);

  // openModal은 고정 레퍼런스가 좋습니다 (렌더마다 안 바뀌게)
  const openModal = useCallback(
    (
      content: React.ReactNode,
      draggable?: boolean,
      onClose?: () => void,
      disableEscape?: boolean,
    ): string => {
      const id = `modal_${Date.now()}_${Math.random()}`;
      const contentRef = React.createRef<HTMLDivElement>();
      // draggable이면 nodeRef를 하나 만든다.
      const dragRef = draggable ? React.createRef<HTMLDivElement>() : undefined;

      const wrapped = (
        <div ref={contentRef} className="global-modal-container">
          {content}
        </div>
      );

      setModals((prev) => [
        ...prev,
        {
          id,
          content: wrapped,
          onClose,
          ref: contentRef,
          dragRef,
          draggable,
          disableEscape,
        },
      ]);

      return id;
    },
    [],
  );

  const closeModal = useCallback((id: string) => {
    setModals((prev) => {
      const modalToClose = prev.find((modal) => modal.id === id);
      if (modalToClose?.onClose) modalToClose.onClose();
      return prev.filter((modal) => modal.id !== id);
    });
  }, []);

  const closeAllModals = useCallback(() => {
    setModals((prev) => {
      prev.forEach((modal) => modal.onClose?.());
      return [];
    });
  }, []);

  const closeTopNModals = useCallback((count: number) => {
    setModals((prev) => {
      const toClose = prev.slice(-count);
      const remaining = prev.slice(0, prev.length - count);
      toClose.forEach((m) => m.onClose?.());
      return remaining;
    });
  }, []);

  const updateModalContent = useCallback(
    (id: string, newContent: React.ReactNode) => {
      setModals((prev) =>
        prev.map((m) => (m.id === id ? { ...m, content: newContent } : m)),
      );
    },
    [],
  );

  // 바깥 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modals.length === 0) return;
      const topModal = modals[modals.length - 1];
      if (topModal.draggable || topModal.disableEscape) return;

      const target = e.target as Node;
      const modalElement = topModal.ref?.current;

      const isOutside = modalElement && !modalElement.contains(target);
      const isInsidePopover =
        document.querySelector('.popover1')?.contains(target) ||
        document.querySelector('.popover2')?.contains(target) ||
        document.querySelector('.base-context-menu')?.contains(target);

      if (isOutside && !isInsidePopover) {
        closeModal(topModal.id);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [modals, closeModal]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();

        if (modals.length > 0) {
          const topModal = modals[modals.length - 1];
          if (topModal.disableEscape) return;
          if (topModal.onClose) topModal.onClose();
          setModals((prev) => prev.slice(0, -1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modals]);

  const contextValue: ModalContextType = {
    isModalOpen,
    setModalOpen,
    openModal,
    updateModalContent,
    closeModal,
    closeAllModals,
    closeTopNModals,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modals.length > 0 &&
        ReactDOM.createPortal(
          <>
            {modals.map((modal, index) => {
              const zIndex = 10000 + index * 10;
              if (modal.draggable) {
                return (
                  <Draggable
                    key={modal.id}
                    handle=".global-modal-container2"
                    nodeRef={modal.dragRef as any} // ★ findDOMNode 방지
                  >
                    <div ref={modal.dragRef} className="global-modal-overlay2">
                      <FocusLock>
                        <div className="global-modal-container2">
                          {modal.content}
                        </div>
                      </FocusLock>
                    </div>
                  </Draggable>
                );
              } else {
                return (
                  <div
                    key={modal.id}
                    className="global-modal-overlay"
                    style={{ zIndex: zIndex }}
                    tabIndex={-1}
                    onKeyDownCapture={(event) => {
                      event.stopPropagation();
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        if (!modal.disableEscape) {
                          modal.onClose?.();
                          closeModal(modal.id);
                        }
                      }
                    }}
                  >
                    <FocusLock>
                      {/* <div
                        className="global-modal-container"
                        style={{ zIndex: zIndex + 1 }}
                      > */}
                        {modal.content}
                      {/* </div> */}
                    </FocusLock>
                  </div>
                );
              }
            })}
          </>,
          document.body,
        )}
    </ModalContext.Provider>
  );
};
